import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config/api';
import * as XLSX from 'xlsx';
import './GestionProducto.css';

const hoyISO = () => {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
};

const formNuevoVacio = () => ({
    tipoMov: '+', idSucursal: 1, fecha: hoyISO(), idBanco: '',
    usaContraCta: false, idConcepto: '', tipoDoc: '', nroDoc: '',
    monto: '', idMoneda: 'GS', tipocambio: 1, vencimiento: hoyISO(),
    idClaspago: '', detalle: ''
});

const formatearFecha = (fecha) => {
    if (!fecha) return '';
    const s = String(fecha).substring(0, 10);
    const [year, month, day] = s.split('-');
    if (!day) return s;
    return `${day}/${month}/${year}`;
};

const formatearNumero = (valor, decimales = 0) => {
    if (valor == null) return '0';
    return Number(valor).toLocaleString('es-PY', { minimumFractionDigits: decimales, maximumFractionDigits: decimales });
};

function OperacionesCajaBanco({ usuario }) {
    // Catalogos
    const [bancos, setBancos] = useState([]);
    const [sucursales, setSucursales] = useState([]);
    const [monedas, setMonedas] = useState([]);
    const [tiposDoc, setTiposDoc] = useState([]);
    const [clasesPago, setClasesPago] = useState([]);

    // Alta / edicion de operacion
    const [modalNuevo, setModalNuevo] = useState(false);
    const [editandoId, setEditandoId] = useState(null);
    const [form, setForm] = useState(formNuevoVacio());
    const [guardando, setGuardando] = useState(false);

    // Cheques adelantados
    const [chequesOp, setChequesOp] = useState([]);
    const [modalCheques, setModalCheques] = useState(false);
    const [chequesDisp, setChequesDisp] = useState([]);

    // Selector de cuenta (banco)
    const [bancoSeleccionado, setBancoSeleccionado] = useState(null);
    const [busquedaBanco, setBusquedaBanco] = useState('');
    const [dropdownBancoAbierto, setDropdownBancoAbierto] = useState(false);
    const dropdownBancoRef = useRef(null);

    // Filtros de fecha (por defecto: dia actual)
    const [desde, setDesde] = useState(hoyISO());
    const [hasta, setHasta] = useState(hoyISO());

    // Datos
    const [operaciones, setOperaciones] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [yaBusco, setYaBusco] = useState(false);
    const [busqueda, setBusqueda] = useState('');

    // Paginacion
    const [paginaActual, setPaginaActual] = useState(1);
    const [itemsPorPagina, setItemsPorPagina] = useState(20);

    useEffect(() => {
        Promise.all([
            axios.get(getApiUrl('/api/bancos')),
            axios.get(getApiUrl('/api/sucursales')),
            axios.get(getApiUrl('/api/monedas')),
            axios.get(getApiUrl('/api/operaciones-cb/tipos-doc')),
            axios.get(getApiUrl('/api/operaciones-cb/clases-pago'))
        ]).then(([rb, rs, rm, rt, rc]) => {
            setBancos(rb.data || []);
            setSucursales(rs.data || []);
            setMonedas(rm.data || []);
            setTiposDoc(rt.data || []);
            setClasesPago(rc.data || []);
        }).catch(e => console.error('Error catalogos:', e));
    }, []);

    // Cargar por defecto las operaciones del dia actual al abrir la pantalla
    useEffect(() => {
        buscar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Cerrar dropdown al click afuera
    useEffect(() => {
        const handler = (e) => {
            if (dropdownBancoRef.current && !dropdownBancoRef.current.contains(e.target)) setDropdownBancoAbierto(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const filtrarBancos = (b) => {
        const t = (b || '').toLowerCase();
        if (!t) return bancos;
        return bancos.filter(x =>
            String(x.idBanco || '').toLowerCase().includes(t) ||
            String(x.nomCta || '').toLowerCase().includes(t) ||
            String(x.nomBco || '').toLowerCase().includes(t));
    };

    const seleccionarBanco = (b) => {
        setBancoSeleccionado(b);
        setBusquedaBanco('');
        setDropdownBancoAbierto(false);
    };

    const buscar = async () => {
        setCargando(true); setYaBusco(true); setPaginaActual(1);
        try {
            const params = {};
            if (bancoSeleccionado) params.idBanco = bancoSeleccionado.idBanco;
            if (desde) params.desde = desde;
            if (hasta) params.hasta = hasta;
            const r = await axios.get(getApiUrl('/api/operaciones-cb'), { params });
            setOperaciones(r.data || []);
        } catch (e) {
            console.error('Error operaciones:', e);
            alert('Error al buscar operaciones: ' + (e.response?.data || e.message));
            setOperaciones([]);
        } finally {
            setCargando(false);
        }
    };

    // Filtro de texto sobre los resultados
    const operacionesFiltradas = () => {
        const b = busqueda.trim().toLowerCase();
        if (!b) return operaciones;
        return operaciones.filter(o =>
            String(o.id_operacion || '').includes(b) ||
            String(o.banco || '').toLowerCase().includes(b) ||
            String(o.tipo_doc || '').toLowerCase().includes(b) ||
            String(o.nro_doc || '').includes(b) ||
            String(o.detalle || '').toLowerCase().includes(b));
    };

    const filtradas = operacionesFiltradas();

    // Totales
    const totIngreso = filtradas.reduce((s, o) => s + (Number(o.ingreso) || 0), 0);
    const totEgreso = filtradas.reduce((s, o) => s + (Number(o.egreso) || 0), 0);

    // Paginacion
    const totalPaginas = Math.max(1, Math.ceil(filtradas.length / itemsPorPagina));
    const indicePrimero = (paginaActual - 1) * itemsPorPagina;
    const indiceUltimo = indicePrimero + itemsPorPagina;
    const actuales = filtradas.slice(indicePrimero, indiceUltimo);

    // ===== Alta / edicion de operacion =====
    const abrirModalNuevo = () => {
        const f = formNuevoVacio();
        // Si hay una cuenta ya seleccionada en el filtro, precargarla
        if (bancoSeleccionado) {
            f.idBanco = bancoSeleccionado.idBanco;
            if (bancoSeleccionado.idMoneda) f.idMoneda = bancoSeleccionado.idMoneda;
        }
        setForm(f);
        setEditandoId(null);
        setChequesOp([]);
        setModalNuevo(true);
    };

    const abrirModalEditar = async (op) => {
        try {
            const r = await axios.get(getApiUrl('/api/operaciones-cb/' + op.id_operacion));
            const d = r.data;
            const esIngreso = d.tipo_mov === '+';
            const tieneCtra = d.id_concepto && String(d.id_concepto).trim() !== '' && d.ctracta;
            setForm({
                tipoMov: d.tipo_mov || '+',
                idSucursal: d.id_sucursal || 1,
                fecha: d.fecha ? String(d.fecha).substring(0, 10) : hoyISO(),
                idBanco: d.id_banco || '',
                usaContraCta: !!tieneCtra,
                idConcepto: tieneCtra ? String(d.id_concepto).trim() : '',
                tipoDoc: d.tipo_doc || '',
                nroDoc: d.nro_doc ?? '',
                monto: esIngreso ? (d.ingreso ?? '') : (d.egreso ?? ''),
                idMoneda: d.id_moneda || 'GS',
                tipocambio: d.tipocambio ?? 1,
                vencimiento: d.vencimiento ? String(d.vencimiento).substring(0, 10) : '',
                idClaspago: d.id_claspago ? String(d.id_claspago).trim() : '',
                detalle: d.detalle || ''
            });
            setEditandoId(op.id_operacion);
            await cargarChequesOp(op.id_operacion);
            setModalNuevo(true);
        } catch (e) {
            console.error('Error cargando operacion:', e);
            alert('No se pudo cargar la operación: ' + (e.response?.data || e.message));
        }
    };

    const eliminarOperacion = async (op) => {
        if (!window.confirm(`¿Eliminar la operación Nro ${op.id_operacion}? Esta acción no se puede deshacer.`)) return;
        try {
            await axios.delete(getApiUrl('/api/operaciones-cb/' + op.id_operacion));
            alert('Operación eliminada.');
            if (yaBusco) buscar();
        } catch (e) {
            console.error('Error eliminando:', e);
            alert('No se pudo eliminar: ' + (e.response?.data || e.message));
        }
    };

    const imprimirComprobante = (op) => {
        window.open(getApiUrl('/api/operaciones-cb/imprimir/' + op.id_operacion), '_blank');
    };

    const imprimirExtracto = () => {
        if (!bancoSeleccionado) { alert('Seleccioná una cuenta (Caja/Banco) para generar el extracto.'); return; }
        if (!desde || !hasta) { alert('Ingresá el rango de fechas Desde y Hasta.'); return; }
        const params = new URLSearchParams({ idBanco: bancoSeleccionado.idBanco, desde, hasta });
        window.open(getApiUrl('/api/operaciones-cb/extracto') + '?' + params.toString(), '_blank');
    };

    // ===== Cheques adelantados =====
    const cargarChequesOp = async (idOperacion) => {
        try {
            const r = await axios.get(getApiUrl('/api/operaciones-cb/' + idOperacion + '/cheques'));
            setChequesOp(r.data || []);
        } catch (e) { console.error('Error cheques op:', e); setChequesOp([]); }
    };

    const abrirModalCheques = async () => {
        try {
            const r = await axios.get(getApiUrl('/api/operaciones-cb/cheques-disponibles'));
            setChequesDisp(r.data || []);
            setModalCheques(true);
        } catch (e) { console.error('Error cheques disp:', e); alert('No se pudieron cargar los cheques disponibles.'); }
    };

    const afectarCheque = async (ch) => {
        try {
            await axios.post(getApiUrl('/api/operaciones-cb/' + editandoId + '/afectar-cheque'), {
                idCliente: ch.id_cliente, idBanco: ch.id_banco, nrocheque: ch.nrocheque
            });
            await cargarChequesOp(editandoId);
            setChequesDisp(prev => prev.filter(x => !(x.id_cliente === ch.id_cliente && x.id_banco === ch.id_banco && x.nrocheque === ch.nrocheque)));
        } catch (e) { alert('No se pudo afectar el cheque: ' + (e.response?.data || e.message)); }
    };

    const desafectarCheque = async (ch) => {
        try {
            await axios.post(getApiUrl('/api/operaciones-cb/desafectar-cheque'), {
                idCliente: ch.id_cliente, idBanco: ch.id_banco, nrocheque: ch.nrocheque
            });
            await cargarChequesOp(editandoId);
        } catch (e) { alert('No se pudo desafectar el cheque: ' + (e.response?.data || e.message)); }
    };

    const setFormCampo = (campo, valor) => setForm(prev => ({ ...prev, [campo]: valor }));

    const onCambiarBanco = (idBanco) => {
        const b = bancos.find(x => String(x.idBanco) === String(idBanco));
        setForm(prev => ({ ...prev, idBanco, idMoneda: b?.idMoneda || prev.idMoneda }));
    };

    const onCambiarMoneda = (idMoneda) => {
        const m = monedas.find(x => x.idMoneda === idMoneda);
        setForm(prev => ({ ...prev, idMoneda, tipocambio: m?.cambio || prev.tipocambio || 1 }));
    };

    const guardarOperacion = async () => {
        if (!form.idBanco) { alert('Seleccioná la cuenta de Caja/Banco'); return; }
        if (!form.fecha) { alert('Ingresá la fecha'); return; }
        if (!form.tipoDoc) { alert('Seleccioná el tipo de documento'); return; }
        if (!form.idMoneda) { alert('Seleccioná la moneda'); return; }
        if (!(Number(form.monto) > 0)) { alert('El monto debe ser mayor a 0'); return; }
        if (form.usaContraCta && !form.idConcepto) { alert('Seleccioná la contra cuenta'); return; }

        setGuardando(true);
        try {
            const body = {
                tipoMov: form.tipoMov,
                idSucursal: form.idSucursal,
                fecha: form.fecha,
                idBanco: form.idBanco,
                idConcepto: form.usaContraCta ? form.idConcepto : '',
                tipoDoc: form.tipoDoc,
                nroDoc: Number(form.nroDoc) || 0,
                monto: Number(form.monto),
                idMoneda: form.idMoneda,
                tipocambio: Number(form.tipocambio) || 1,
                vencimiento: form.vencimiento || '',
                idClaspago: form.idClaspago || '',
                detalle: form.detalle || '',
                idOperador: usuario?.idUsuario || usuario?.mail || usuario?.usuario || ''
            };
            let r;
            if (editandoId) {
                r = await axios.put(getApiUrl('/api/operaciones-cb/' + editandoId), body);
            } else {
                r = await axios.post(getApiUrl('/api/operaciones-cb'), body);
            }
            setModalNuevo(false);
            alert((editandoId ? 'Operación modificada. Nro: ' : 'Operación guardada. Nro: ') + (r.data?.idOperacion ?? editandoId ?? ''));
            // Refrescar grilla si corresponde a la cuenta filtrada
            if (yaBusco) buscar();
        } catch (e) {
            console.error('Error guardando operacion:', e);
            alert('No se pudo guardar: ' + (e.response?.data || e.message));
        } finally {
            setGuardando(false);
        }
    };

    const exportarExcel = () => {
        const datos = filtradas.map(o => ({
            'Nro. Op.': o.id_operacion,
            'Fecha': formatearFecha(o.fecha),
            'Cod Cta': o.id_banco,
            'Nom Cta': o.banco,
            'Tipo Doc': o.tipo_doc,
            'Nro Doc': o.nro_doc,
            'Ingreso': Number(o.ingreso) || 0,
            'Egreso': Number(o.egreso) || 0,
            'Moneda': o.id_moneda,
            'T. Cambio': Number(o.tipocambio) || 0,
            'Detalle': o.detalle
        }));
        const ws = XLSX.utils.json_to_sheet(datos);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Operaciones');
        XLSX.writeFile(wb, 'operaciones_caja_banco.xlsx');
    };

    return (
        <div className="gestion-producto-container">
            <div className="header-seccion">
                <h2>Operaciones Caja/Banco</h2>
                <div className="acciones-header">
                    <input type="text" className="input-busqueda" placeholder="Filtrar resultados..."
                        value={busqueda} onChange={(e) => { setBusqueda(e.target.value); setPaginaActual(1); }} />
                    <button className="btn btn-nuevo" onClick={abrirModalNuevo}>➕ Nueva Operación</button>
                    <button className="btn btn-exportar" onClick={imprimirExtracto}
                        style={{ background: '#6A1B9A' }}
                        title="Extracto de la cuenta seleccionada (PDF) en el rango de fechas">📄 Extracto PDF</button>
                    <button className="btn btn-exportar" onClick={exportarExcel} disabled={filtradas.length === 0}>Exportar Excel</button>
                </div>
            </div>

            {/* Filtros de busqueda */}
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '15px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px', marginBottom: '15px', flexWrap: 'wrap' }}>
                <div style={{ minWidth: '320px' }}>
                    <label style={{ display: 'block', fontSize: '13px', color: '#555', fontWeight: 500, marginBottom: '4px' }}>Cuenta (Caja/Banco)</label>
                    <div className="cuenta-selector" ref={dropdownBancoRef}>
                        {bancoSeleccionado ? (
                            <div className="cuenta-seleccionada">
                                <span>{bancoSeleccionado.idBanco} - {bancoSeleccionado.nomCta}</span>
                                <button type="button" onClick={() => setBancoSeleccionado(null)} className="btn-limpiar">✕</button>
                            </div>
                        ) : (
                            <input type="text" placeholder="Buscar cuenta... (vacío = todas)" value={busquedaBanco}
                                onChange={(e) => setBusquedaBanco(e.target.value)} onFocus={() => setDropdownBancoAbierto(true)}
                                className="form-input" />
                        )}
                        {dropdownBancoAbierto && !bancoSeleccionado && (
                            <div className="cuenta-dropdown">
                                {filtrarBancos(busquedaBanco).map((b, i) => (
                                    <div key={i} className="cuenta-item" onClick={() => seleccionarBanco(b)}>
                                        <strong>{b.idBanco}</strong> - {b.nomCta} <span style={{ color: '#7f8c8d' }}>({b.nomBco})</span>
                                    </div>
                                ))}
                                {filtrarBancos(busquedaBanco).length === 0 && (
                                    <div className="cuenta-item" style={{ color: '#7f8c8d' }}>Sin resultados</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '13px', color: '#555', fontWeight: 500, marginBottom: '4px' }}>Desde</label>
                    <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="form-input" />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '13px', color: '#555', fontWeight: 500, marginBottom: '4px' }}>Hasta</label>
                    <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="form-input" />
                </div>
                <button className="btn btn-nuevo" onClick={buscar} disabled={cargando}>
                    {cargando ? 'Buscando...' : '🔍 Buscar'}
                </button>
            </div>

            {/* Grilla de operaciones */}
            <div className="tabla-container">
                <table className="tabla-datos">
                    <thead>
                        <tr>
                            <th>Nro. Op.</th>
                            <th>Fecha</th>
                            <th>Cod Cta</th>
                            <th>Nom Cta</th>
                            <th>Tipo Doc</th>
                            <th style={{ textAlign: 'right' }}>Nro Doc</th>
                            <th style={{ textAlign: 'right' }}>Ingreso</th>
                            <th style={{ textAlign: 'right' }}>Egreso</th>
                            <th>Moneda</th>
                            <th style={{ textAlign: 'right' }}>T. Cambio</th>
                            <th>Detalle</th>
                            <th>Acc</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cargando ? <tr><td colSpan="12" className="sin-datos">Cargando...</td></tr>
                        : !yaBusco ? <tr><td colSpan="12" className="sin-datos">Elegí los filtros y presioná Buscar</td></tr>
                        : actuales.length === 0 ? <tr><td colSpan="12" className="sin-datos">No hay operaciones</td></tr>
                        : actuales.map((o, i) => (
                            <tr key={i}>
                                <td>{o.id_operacion}</td>
                                <td>{formatearFecha(o.fecha)}</td>
                                <td>{o.id_banco}</td>
                                <td>{o.banco}</td>
                                <td>{o.tipo_doc}</td>
                                <td style={{ textAlign: 'right' }}>{o.nro_doc}</td>
                                <td style={{ textAlign: 'right', color: '#1b5e20' }}>{Number(o.ingreso) ? formatearNumero(o.ingreso) : ''}</td>
                                <td style={{ textAlign: 'right', color: '#c62828' }}>{Number(o.egreso) ? formatearNumero(o.egreso) : ''}</td>
                                <td>{o.id_moneda}</td>
                                <td style={{ textAlign: 'right' }}>{formatearNumero(o.tipocambio)}</td>
                                <td>{o.detalle}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                    <button type="button" onClick={() => abrirModalEditar(o)} className="btn-accion" title="Editar operación">✏️</button>
                                    <button type="button" onClick={() => imprimirComprobante(o)} className="btn-accion" title="Imprimir comprobante" style={{ color: '#2DCCD3' }}>🖨️</button>
                                    <button type="button" onClick={() => eliminarOperacion(o)} className="btn-accion" title="Eliminar operación" style={{ color: '#c62828' }}>✕</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    {filtradas.length > 0 && (
                        <tfoot>
                            <tr style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                <td colSpan="6" style={{ textAlign: 'right' }}>Totales:</td>
                                <td style={{ textAlign: 'right', color: '#1b5e20' }}>{formatearNumero(totIngreso)}</td>
                                <td style={{ textAlign: 'right', color: '#c62828' }}>{formatearNumero(totEgreso)}</td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>

            {/* Paginacion */}
            {filtradas.length > 0 && (
                <div className="paginacion-container">
                    <div className="paginacion-info">
                        <span>Mostrando {indicePrimero + 1} a {Math.min(indiceUltimo, filtradas.length)} de {filtradas.length}</span>
                        <select value={itemsPorPagina} onChange={(e) => { setItemsPorPagina(Number(e.target.value)); setPaginaActual(1); }} className="select-registros">
                            <option value={10}>10</option><option value={20}>20</option><option value={50}>50</option><option value={100}>100</option>
                        </select>
                    </div>
                    <div className="paginacion-controles">
                        <button onClick={() => setPaginaActual(1)} disabled={paginaActual === 1} className="btn-paginacion">|&lt;</button>
                        <button onClick={() => setPaginaActual(p => p - 1)} disabled={paginaActual === 1} className="btn-paginacion">&lt;</button>
                        <button onClick={() => setPaginaActual(p => p + 1)} disabled={paginaActual >= totalPaginas} className="btn-paginacion">&gt;</button>
                        <button onClick={() => setPaginaActual(totalPaginas)} disabled={paginaActual >= totalPaginas} className="btn-paginacion">&gt;|</button>
                    </div>
                </div>
            )}

            {/* ====== MODAL NUEVA OPERACION ====== */}
            {modalNuevo && (
                <div className="modal-overlay" onClick={() => !guardando && setModalNuevo(false)}>
                    <div className="modal-contenido-grande" style={{ maxWidth: '760px', width: '92vw' }} onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{editandoId ? `Editar Operación Nro ${editandoId}` : 'Nueva Operación de Caja/Banco'}</h3>
                            <button onClick={() => setModalNuevo(false)} className="btn-cerrar">&times;</button>
                        </div>
                        <div className="modal-body">
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', columnGap: '14px', rowGap: '10px' }}>
                                <div className="form-group">
                                    <label>Tipo *</label>
                                    <select className="form-input" value={form.tipoMov} onChange={(e) => setFormCampo('tipoMov', e.target.value)}>
                                        <option value="+">Ingreso</option>
                                        <option value="-">Egreso</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Sucursal *</label>
                                    <select className="form-input" value={form.idSucursal} onChange={(e) => setFormCampo('idSucursal', Number(e.target.value))}>
                                        {sucursales.map(s => <option key={s.idSucursal} value={s.idSucursal}>{s.nombre}</option>)}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Cuenta (Caja/Banco) *</label>
                                    <select className="form-input" value={form.idBanco} onChange={(e) => onCambiarBanco(e.target.value)}>
                                        <option value="">-- Seleccione --</option>
                                        {bancos.map((b, i) => <option key={i} value={b.idBanco}>{b.idBanco} - {b.nomCta}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Fecha *</label>
                                    <input type="date" className="form-input" value={form.fecha} onChange={(e) => setFormCampo('fecha', e.target.value)} />
                                </div>

                                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                        <input type="checkbox" checked={form.usaContraCta}
                                            onChange={(e) => setFormCampo('usaContraCta', e.target.checked)} />
                                        Usar contra cuenta ({form.tipoMov === '+' ? 'Acreedora' : 'Deudora'})
                                    </label>
                                    {form.usaContraCta && (
                                        <select className="form-input" style={{ marginTop: '6px' }} value={form.idConcepto}
                                            onChange={(e) => setFormCampo('idConcepto', e.target.value)}>
                                            <option value="">-- Seleccione contra cuenta --</option>
                                            {bancos.map((b, i) => <option key={i} value={b.idBanco}>{b.idBanco} - {b.nomCta}</option>)}
                                        </select>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label>Tipo de Documento *</label>
                                    <select className="form-input" value={form.tipoDoc} onChange={(e) => setFormCampo('tipoDoc', e.target.value)}>
                                        <option value="">-- Seleccione --</option>
                                        {tiposDoc.map((t, i) => <option key={i} value={t.id_tipodoc}>{t.nom_tipodoc}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Nro Documento</label>
                                    <input type="number" className="form-input" value={form.nroDoc} onChange={(e) => setFormCampo('nroDoc', e.target.value)} />
                                </div>

                                <div className="form-group">
                                    <label>Monto *</label>
                                    <input type="number" className="form-input" value={form.monto} onChange={(e) => setFormCampo('monto', e.target.value)}
                                        style={{ textAlign: 'right' }} />
                                </div>
                                <div className="form-group">
                                    <label>Vencimiento</label>
                                    <input type="date" className="form-input" value={form.vencimiento} onChange={(e) => setFormCampo('vencimiento', e.target.value)} />
                                </div>

                                <div className="form-group">
                                    <label>Moneda *</label>
                                    <select className="form-input" value={form.idMoneda} onChange={(e) => onCambiarMoneda(e.target.value)}>
                                        {monedas.map(m => <option key={m.idMoneda} value={m.idMoneda}>{m.nomMoneda}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Tipo de Cambio *</label>
                                    <input type="number" className="form-input" value={form.tipocambio} onChange={(e) => setFormCampo('tipocambio', e.target.value)}
                                        style={{ textAlign: 'right' }} />
                                </div>

                                <div className="form-group">
                                    <label>Clase de Pago</label>
                                    <select className="form-input" value={form.idClaspago} onChange={(e) => setFormCampo('idClaspago', e.target.value)}>
                                        <option value="">-- Seleccione --</option>
                                        {clasesPago.map((c, i) => <option key={i} value={c.id_claspago}>{c.nom_claspago}</option>)}
                                    </select>
                                </div>
                                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                    <label>Concepto / Detalle</label>
                                    <input type="text" className="form-input" maxLength={140} value={form.detalle} onChange={(e) => setFormCampo('detalle', e.target.value)} />
                                </div>
                            </div>

                            {/* Cheques adelantados (solo en edicion) */}
                            {editandoId && (
                                <div style={{ marginTop: '18px', borderTop: '1px solid #e0e0e0', paddingTop: '12px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                        <strong>Cheques adelantados afectados</strong>
                                        <button type="button" onClick={abrirModalCheques}
                                            style={{ background: '#0d6efd', color: '#fff', border: 'none', borderRadius: '4px', padding: '5px 12px', fontSize: '12px', cursor: 'pointer', fontWeight: 600 }}>
                                            + Afectar cheque
                                        </button>
                                    </div>
                                    {chequesOp.length === 0 ? (
                                        <p style={{ fontSize: '12px', color: '#7f8c8d', margin: 0 }}>No hay cheques afectados a esta operación.</p>
                                    ) : (
                                        <table className="tabla-datos" style={{ width: '100%', fontSize: '12px' }}>
                                            <thead>
                                                <tr><th>Banco</th><th>Cliente</th><th>Nro Cheque</th><th>Vencim.</th><th style={{ textAlign: 'right' }}>Monto</th><th></th></tr>
                                            </thead>
                                            <tbody>
                                                {chequesOp.map((ch, i) => (
                                                    <tr key={i}>
                                                        <td>{ch.banco || ch.id_banco}</td>
                                                        <td>{ch.nom_cliente || ch.id_cliente}</td>
                                                        <td>{ch.nrocheque}</td>
                                                        <td>{formatearFecha(ch.vencimiento)}</td>
                                                        <td style={{ textAlign: 'right' }}>{formatearNumero(ch.monto)}</td>
                                                        <td>
                                                            <button type="button" onClick={() => desafectarCheque(ch)}
                                                                style={{ background: '#c62828', color: '#fff', border: 'none', borderRadius: '4px', padding: '3px 8px', fontSize: '11px', cursor: 'pointer' }}>
                                                                Desafectar
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            )}
                            <div className="form-actions">
                                <button type="button" className="btn-secondary" onClick={() => setModalNuevo(false)} disabled={guardando}>Cancelar</button>
                                <button type="button" className="btn-primary" onClick={guardarOperacion} disabled={guardando}>
                                    {guardando ? 'Guardando...' : (editandoId ? 'Modificar' : 'Guardar')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ====== MODAL AFECTAR CHEQUES DISPONIBLES ====== */}
            {modalCheques && (
                <div className="modal-overlay" style={{ zIndex: 2100 }} onClick={() => setModalCheques(false)}>
                    <div className="modal-contenido-grande" style={{ maxWidth: '820px', width: '92vw' }} onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Cheques adelantados disponibles</h3>
                            <button onClick={() => setModalCheques(false)} className="btn-cerrar">&times;</button>
                        </div>
                        <div className="modal-body">
                            {chequesDisp.length === 0 ? (
                                <p style={{ textAlign: 'center', color: '#7f8c8d' }}>No hay cheques disponibles para afectar.</p>
                            ) : (
                                <div style={{ maxHeight: '55vh', overflowY: 'auto' }}>
                                    <table className="tabla-datos" style={{ width: '100%', fontSize: '13px' }}>
                                        <thead>
                                            <tr><th>Banco</th><th>Cliente</th><th>Nro Cheque</th><th>Fecha</th><th>Vencim.</th><th style={{ textAlign: 'right' }}>Monto</th><th></th></tr>
                                        </thead>
                                        <tbody>
                                            {chequesDisp.map((ch, i) => (
                                                <tr key={i}>
                                                    <td>{ch.banco || ch.id_banco}</td>
                                                    <td>{ch.nom_cliente || ch.id_cliente}</td>
                                                    <td>{ch.nrocheque}</td>
                                                    <td>{formatearFecha(ch.fecha)}</td>
                                                    <td>{formatearFecha(ch.vencimiento)}</td>
                                                    <td style={{ textAlign: 'right' }}>{formatearNumero(ch.monto)}</td>
                                                    <td>
                                                        <button type="button" onClick={() => afectarCheque(ch)}
                                                            style={{ background: '#198754', color: '#fff', border: 'none', borderRadius: '4px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer', fontWeight: 600 }}>
                                                            Afectar
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            <div className="form-actions">
                                <button type="button" className="btn-secondary" onClick={() => setModalCheques(false)}>Cerrar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default OperacionesCajaBanco;
